"use client";

import { useState } from "react";
import { ChevronDown, ChevronRight, Edit2, Trash2 } from "lucide-react";
import type { TabItem, Form6Config } from "../types";
import { ColorPicker } from "@/components/atoms/colorpicker";

interface TabHeaderProps {
  tab: TabItem;
  isExpanded: boolean;
  rowCount: number;
  colCount: number;
  config: Form6Config;
  onToggleExpand: () => void;
  onUpdateStyle: (
    tabId: number,
    name: string,
    textColor?: string,
    bgColor?: string,
    activeTextColor?: string,
    activeBgColor?: string
  ) => void;
  onDelete: (tabId: number) => void;
}

const TabHeader = ({
  tab,
  isExpanded,
  rowCount,
  colCount,
  config,
  onToggleExpand,
  onUpdateStyle,
  onDelete,
}: TabHeaderProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState(tab.name);
  const [editingTextColor, setEditingTextColor] = useState(
    tab.text_color || ""
  );
  const [editingBgColor, setEditingBgColor] = useState(tab.bg_color || "");
  const [editingActiveTextColor, setEditingActiveTextColor] = useState(
    tab.active_text_color || ""
  );
  const [editingActiveBgColor, setEditingActiveBgColor] = useState(
    tab.active_bg_color || ""
  );

  const handleSave = () => {
    onUpdateStyle(
      tab.id,
      editingName,
      editingTextColor,
      editingBgColor,
      editingActiveTextColor,
      editingActiveBgColor
    );
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditingName(tab.name);
    setEditingTextColor(tab.text_color || "");
    setEditingBgColor(tab.bg_color || "");
    setEditingActiveTextColor(tab.active_text_color || "");
    setEditingActiveBgColor(tab.active_bg_color || "");
    setIsEditing(false);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingName(tab.name);
    setEditingTextColor(tab.text_color || "");
    setEditingBgColor(tab.bg_color || "");
    setEditingActiveTextColor(tab.active_text_color || "");
    setEditingActiveBgColor(tab.active_bg_color || "");
    setIsEditing(true);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(tab.id);
  };

  return (
    <div
      className="flex items-center justify-between px-4 py-3 cursor-pointer transition-colors"
      style={{
        backgroundColor: isExpanded
          ? tab.active_bg_color || "#e5e7eb"
          : tab.bg_color || "#f9fafb",
        color: isExpanded
          ? tab.active_text_color || tab.text_color || undefined
          : tab.text_color || undefined,
      }}
      onClick={onToggleExpand}
    >
      <div className="flex items-center gap-2">
        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-500" />
        )}

        {isEditing ? (
          <div
            className="flex flex-col gap-2 p-3 bg-white rounded shadow-lg border min-w-[280px] text-gray-900"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Name Input */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500 font-medium">Name</label>
              <input
                type="text"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm font-medium text-gray-900"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                  if (e.key === "Escape") handleCancel();
                }}
              />
            </div>

            {/* Color Inputs - only show if enabled */}
            {config.showTabStyling && (
              <div className="flex flex-col gap-1">
                <ColorPicker
                  label="Text"
                  value={editingTextColor}
                  onChange={setEditingTextColor}
                  defaultColor="#000000"
                />
                <ColorPicker
                  label="BG"
                  value={editingBgColor}
                  onChange={setEditingBgColor}
                  defaultColor="#f9fafb"
                />
                <ColorPicker
                  label="Active Text"
                  value={editingActiveTextColor}
                  onChange={setEditingActiveTextColor}
                  defaultColor="#000000"
                />
                <ColorPicker
                  label="Active BG"
                  value={editingActiveBgColor}
                  onChange={setEditingActiveBgColor}
                  defaultColor="#e5e7eb"
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-center gap-2 pt-1">
              <button
                onClick={handleSave}
                className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="px-3 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <span
              className="font-medium"
              style={{ color: tab.text_color || undefined }}
            >
              {tab.name}
            </span>
            <span className="text-xs text-gray-400">
              ({rowCount} {config.rowsLabel?.toLowerCase()}, {colCount}{" "}
              {config.columnsLabel?.toLowerCase()})
            </span>
          </>
        )}
      </div>

      <div className="flex items-center gap-1">
        {!isEditing && (
          <button
            onClick={handleEdit}
            className="p-1 hover:bg-blue-100 rounded text-blue-500"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={handleDelete}
          className="p-1 hover:bg-red-100 rounded text-red-500"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default TabHeader;
